import {
    concatHex,
    getAddress,
    isAddressEqual,
    keccak256,
    stringToHex,
    type Address,
    type Hex,
} from 'viem';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type {
    AssociationSignature,
    AuthorizationRegistryChallenge,
    AuthorizationRegistryProvision,
    OwnerBindingTypedData,
} from '@/lib/api/authRegistry';
import { custodialManagerFor } from '@/lib/config/custodialManagers';
import { eoaBootstrapHash } from '@/lib/eoaBootstrapHash';

export type OAuthBindingRoute = 'existing-wallet' | 'new-wallet';

/** On-chain owner/wallet pair a login hash is already registered to. */
export interface AccountHashBinding {
    owner: Address;
    wallet: Address;
}

/**
 * The login is already registered to a different owner's wallet. The manager
 * refuses to re-register a live hash (`M:HASH_IN_USE`), so the only way forward
 * is for that owner to unlink it first.
 */
export class OAuthAccountHashInUseError extends Error {
    readonly code = 'oauthAccountHashInUse';

    constructor(
        readonly registeredOwner: Address,
        readonly registeredWallet: Address,
    ) {
        // the owner EOA is public on-chain data, so naming it is what makes the
        // dead end actionable instead of mysterious.
        super(
            `This login is already linked to a wallet owned by ${registeredOwner}. Switch to that wallet to unlink it first.`,
        );
        this.name = 'OAuthAccountHashInUseError';
    }
}

export function isOAuthAccountHashInUseError(
    error: unknown,
): error is OAuthAccountHashInUseError {
    return (
        !!error &&
        typeof error === 'object' &&
        (error as { code?: unknown }).code === 'oauthAccountHashInUse'
    );
}

/**
 * The user chose one route (create / link) but the chain says the other one
 * applies. Carries the observed state so the dialog can explain it and offer
 * the route that actually works, rather than silently doing something else.
 */
export class OAuthBindingRouteMismatchError extends Error {
    readonly code = 'oauthBindingRouteMismatch';

    constructor(
        readonly expectedRoute: OAuthBindingRoute,
        readonly actualRoute: OAuthBindingRoute,
        readonly authorizationWallet: Address | null,
    ) {
        super(
            actualRoute === 'existing-wallet'
                ? 'This wallet already owns a Snappy account.'
                : 'This wallet does not own a Snappy account yet.',
        );
        this.name = 'OAuthBindingRouteMismatchError';
    }
}

export function isOAuthBindingRouteMismatchError(
    error: unknown,
): error is OAuthBindingRouteMismatchError {
    return (
        !!error &&
        typeof error === 'object' &&
        (error as { code?: unknown }).code === 'oauthBindingRouteMismatch'
    );
}

interface BindingInput {
    owner: Address;
    accountHash: Hex;
    /** The anchor the authorization wallet lives (or is created) on. */
    anchorChainId?: AnchorChainId;
    /**
     * Route the user explicitly chose. When it disagrees with the chain the
     * binding stops with {@link OAuthBindingRouteMismatchError} instead of
     * quietly running the other one.
     */
    expectedRoute?: OAuthBindingRoute;
}

interface BindingDependencies {
    getAuthorizationWallet: (owner: Address) => Promise<Address | null>;
    /** Which owner/wallet this login hash is already registered to, if any. */
    getAccountHashBinding: (
        accountHash: Hex,
    ) => Promise<AccountHashBinding | null>;
    requestAssociation: (
        authorizationWallet: Address,
        accountHash: Hex,
    ) => Promise<AssociationSignature>;
    addAccountHash: (
        authorizationWallet: Address,
        accountHash: Hex,
        association: AssociationSignature,
    ) => Promise<Hex>;
    waitForReceipt: (transactionHash: Hex) => Promise<void>;
    requestProvisionChallenge: (
        owner: Address,
        accountHash: Hex,
    ) => Promise<AuthorizationRegistryChallenge>;
    signOwnerBinding: (typedData: OwnerBindingTypedData) => Promise<Hex>;
    provisionAuthorizationWallet: (input: {
        owner: Address;
        accountHash: Hex;
        ownerBinding: AuthorizationRegistryChallenge['ownerBinding'] & {
            signature: Hex;
        };
    }) => Promise<AuthorizationRegistryProvision>;
}

export type OAuthAccountBindingResult = {
    route: OAuthBindingRoute;
    authorizationWallet: Address;
};

export async function completeOAuthAccountBinding(
    input: BindingInput,
    dependencies: BindingDependencies,
): Promise<OAuthAccountBindingResult> {
    // both routes end in a registration the manager rejects when the hash is
    // live elsewhere, so read that first: a collision must cost the user no
    // signature and no gas.
    const [existing, registered] = await Promise.all([
        dependencies.getAuthorizationWallet(input.owner),
        dependencies.getAccountHashBinding(input.accountHash),
    ]);
    if (registered && !isAddressEqual(registered.owner, input.owner)) {
        throw new OAuthAccountHashInUseError(
            registered.owner,
            registered.wallet,
        );
    }
    const actualRoute: OAuthBindingRoute = existing
        ? 'existing-wallet'
        : 'new-wallet';
    if (input.expectedRoute && input.expectedRoute !== actualRoute) {
        throw new OAuthBindingRouteMismatchError(
            input.expectedRoute,
            actualRoute,
            existing,
        );
    }

    if (existing) {
        const association = await dependencies.requestAssociation(
            existing,
            input.accountHash,
        );
        const transactionHash = await dependencies.addAccountHash(
            existing,
            input.accountHash,
            association,
        );
        await dependencies.waitForReceipt(transactionHash);
        return {
            route: 'existing-wallet',
            authorizationWallet: existing,
        };
    }

    const anchorChainId = input.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const anchorManager = custodialManagerFor(anchorChainId);
    const challenge = await dependencies.requestProvisionChallenge(
        input.owner,
        input.accountHash,
    );
    const expectedAccountHashesHash = keccak256(
        concatHex([input.accountHash, eoaBootstrapHash(input.owner)]),
    );
    const challengeMatchesRequest =
        isAddressEqual(getAddress(challenge.owner), input.owner) &&
        challenge.accountHash.toLowerCase() ===
            input.accountHash.toLowerCase() &&
        isAddressEqual(
            getAddress(challenge.typedData.message.owner),
            input.owner,
        ) &&
        // the manager is what the signature is SCOPED to: without pinning it, a
        // manipulated response could have the owner authorize a binding against
        // any contract the server names.
        challenge.typedData.domain.chainId === anchorChainId &&
        isAddressEqual(
            getAddress(challenge.typedData.domain.verifyingContract),
            anchorManager,
        ) &&
        isAddressEqual(
            getAddress(challenge.typedData.message.manager),
            anchorManager,
        ) &&
        challenge.typedData.message.accountHashesHash.toLowerCase() ===
            expectedAccountHashesHash.toLowerCase() &&
        challenge.typedData.message.intentHash.toLowerCase() ===
            keccak256(stringToHex('create')).toLowerCase() &&
        challenge.typedData.message.nonce === challenge.ownerBinding.nonce &&
        challenge.typedData.message.issuedAt ===
            challenge.ownerBinding.issuedAt &&
        challenge.typedData.message.expiresAt ===
            challenge.ownerBinding.expiresAt;
    if (!challengeMatchesRequest) {
        throw new Error(
            'The authorization challenge does not match the connected wallet and login.',
        );
    }
    const signature = await dependencies.signOwnerBinding(challenge.typedData);
    const provisioned = await dependencies.provisionAuthorizationWallet({
        owner: input.owner,
        accountHash: input.accountHash,
        ownerBinding: { ...challenge.ownerBinding, signature },
    });
    return {
        route: 'new-wallet',
        authorizationWallet: getAddress(provisioned.authorizationWallet),
    };
}
